import { Box } from '@mui/material';
import { getTenderDetails, getTenders } from 'lib/api/publicMarket';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import { TENDER_STATES } from 'lib/consts';
import TableWithPagination, {
  Header,
} from 'lib/designSystem/TableWithPagination';
import { errorHandler } from 'lib/functions/errors';
import { Tender, TenderType } from 'lib/types/models';
import React, { useEffect } from 'react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { showTenderModal } from 'services/redux/tenderModalSlice';

const BASE_PAGINATION = {
  page: 1,
  pageSize: 25,
  total: 0,
  totalPages: 0,
};

const HEADERS: Header[] = [
  {
    label: 'Código',
    key: 'externalCode',
    customComponent: (value: string) => <Box sx={{ py: 1 }}>{value}</Box>,
  },
  { label: 'Nombre', key: 'name' },
  { label: 'Tipo', key: 'type' },
  { label: 'Estado', key: 'status' },
  { label: 'Mandante', key: 'buyerId' },
  { label: 'Fecha Publicación', key: 'publicationDate', type: 'date' },
  { label: 'Fecha Cierre', key: 'closeDate', type: 'date' },
  { label: 'Fecha Adjudicación', key: 'adjudicationDate', type: 'date' },
  { label: 'Monto Estimado', key: 'estimatedAmount', type: 'money' },
];

const Tenders = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState(BASE_PAGINATION);
  const [data, setData] = useState<Tender[]>([]);
  const [externalCode, setExternalCode] = useState<string>('');
  const [selectedBuyer, setSelectedBuyer] = useState<string>('');
  const [initialDateStr, setInitialDateStr] = useState<string | null>(null);
  const [finalDateStr, setFinalDateStr] = useState<string | null>(null);
  const [status, setStatus] = useState<{ value: string; label: string }[]>([]);
  const [dateOptions, setDateOptions] = useState<string>('publication');
  const [types, setTypes] = useState<{ value: TenderType; label: string }[]>(
    [],
  );

  const buildTendersQuery = (override?: {
    page?: number;
    pageSize?: number;
    useSupplierFilterMode?: boolean;
  }): string => {
    if (!id) throw new Error('Supplier ID is required');
    const params = new URLSearchParams();
    params.append('supplierId', id);
    if (externalCode) params.append('externalCode', externalCode);
    if (selectedBuyer) params.append('buyerId', selectedBuyer);
    if (types.length > 0)
      params.append('types', types.map((t) => t.value).join(','));

    if (initialDateStr)
      params.append(
        dateOptions === 'publication'
          ? 'startPublicationDate'
          : 'startAdjudicationDate',
        initialDateStr,
      );
    if (finalDateStr)
      params.append(
        dateOptions === 'publication'
          ? 'endPublicationDate'
          : 'endAdjudicationDate',
        finalDateStr,
      );
    if (status.length > 0)
      params.append('status', status.map((s) => s.value).join(','));
    const pageToUse = override?.page ?? pagination.page;
    const pageSizeToUse = override?.pageSize ?? pagination.pageSize;
    if (pageToUse) params.append('page', String(pageToUse));
    if (pageSizeToUse) params.append('pageSize', String(pageSizeToUse));
    return params.toString();
  };

  const fetchData = async (params?: { page: number; pageSize: number }) => {
    try {
      setLoading(true);
      const pageToUse = params?.page ?? pagination.page;
      const pageSizeToUse = params?.pageSize ?? pagination.pageSize;
      const queryString = buildTendersQuery({
        page: pageToUse,
        pageSize: pageSizeToUse,
      });
      const response = await getTenders(dispatch, queryString);
      setData(response?.data ?? []);
      setPagination(response?.pagination ?? BASE_PAGINATION);
      return {
        data: response?.data ?? [],
        pagination: response?.pagination ?? BASE_PAGINATION,
      };
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = async (rowId: string) => {
    try {
      if (!rowId) throw new Error('Tender ID is required');
      const tenderDetails = await getTenderDetails(dispatch, rowId);
      if (tenderDetails) dispatch(showTenderModal(tenderDetails));
    } catch (error) {
      errorHandler(dispatch, error);
    }
  };

  const filters: Filter[] = [
    {
      type: 'text',
      label: 'Código',
      value: externalCode,
      setter: setExternalCode,
    },
    {
      type: 'autocomplete',
      label: 'Mandante',
      key: 'buyerId',
      value: selectedBuyer,
      setter: setSelectedBuyer,
    },
    {
      type: 'date',
      label: 'Fecha inicio',
      dateKey: 'startDate',
      value: initialDateStr,
      setter: setInitialDateStr,
    },
    {
      type: 'date',
      label: 'Fecha fin',
      dateKey: 'endDate',
      value: finalDateStr,
      setter: setFinalDateStr,
    },
    {
      type: 'select',
      label: 'Fecha',
      value: dateOptions,
      setter: setDateOptions,
      options: [
        { value: 'publication', label: 'Publicación' },
        { value: 'adjudication', label: 'Adjudicación' },
      ],
    },
    {
      type: 'multiple',
      label: 'Tipo',
      value: types,
      setter: setTypes,
      options: Object.values(TenderType).map((t) => ({
        value: t,
        label: t,
      })),
    },
    {
      type: 'multiple',
      label: 'Estado',
      value: status,
      setter: setStatus,
      options: TENDER_STATES.map((s) => ({
        value: String(s.value),
        label: s.label,
      })),
    },
  ];

  useEffect(() => {
    void fetchData({
      page: pagination.page,
      pageSize: pagination.pageSize,
    });
  }, []);

  return (
    <Box sx={{ py: 2, position: 'relative' }}>
      <Box sx={{ position: 'absolute', top: -36, right: 0 }}>
        <GlobalSearcher
          filters={filters}
          triggerSearch={() => fetchData()}
          dispatch={dispatch}
        />
      </Box>
      <TableWithPagination
        data={data}
        setData={setData}
        dataHeaders={HEADERS}
        loading={loading}
        handleClickRow={handleRowClick}
        pagination={pagination}
        setPagination={setPagination}
        searchData={fetchData}
        exportToFile="xlsx"
      />
    </Box>
  );
};

export default Tenders;
